import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { Github, Loader2, Check, X, Eye, EyeOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signIn, signUp, signInWithSocial } = useAuth();
  
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerCompany, setRegisterCompany] = useState("");
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  
  // Add states for password reset
  const [isResetFlow, setIsResetFlow] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    number: false,
    special: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Add state to track if we're processing a password reset
  const [isProcessingReset, setIsProcessingReset] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  // Modify the redirect effect to not redirect during password reset
  useEffect(() => {
    if (user && !loading && !isProcessingReset) {
      navigate("/");
    }
  }, [user, loading, navigate, isProcessingReset]);
  
  // Check URL for reset token
  useEffect(() => {
    const handlePasswordReset = async () => {
      const fragment = window.location.hash;
      if (fragment && fragment.includes('type=recovery')) {
        setIsProcessingReset(true);
        setIsResetFlow(true);

        try {
          // Get the current session - this will process the recovery tokens automatically
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError || !session) {
            throw new Error(sessionError?.message || 'No session found');
          }

          // Store the tokens
          setResetToken(session.access_token);
        } catch (error) {
          console.error('Error processing reset link:', error);
          toast({
            title: "Error",
            description: "Invalid or expired reset link. Please request a new one.",
            variant: "destructive"
          });
          setIsResetFlow(false);
          navigate('/auth');
        }
      }
    };

    handlePasswordReset();
  }, [navigate]);

  // Password validation for reset
  const [resetPasswordValidation, setResetPasswordValidation] = useState({
    length: false,
    number: false,
    special: false
  });

  useEffect(() => {
    setResetPasswordValidation({
      length: newPassword.length >= 8,
      number: /\d/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
    });
  }, [newPassword]);

  const isResetPasswordValid = Object.values(resetPasswordValidation).every(Boolean);

  // Handle password reset submission
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isResetPasswordValid) {
      toast({
        title: "Invalid Password",
        description: "Please ensure your new password meets all requirements.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "New password and confirmation must match.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('No valid session found');
      }

      const { error } = await supabase.auth.updateUser({ 
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "Password Updated",
        description: "Your password has been reset successfully. Please log in with your new password.",
      });

      // Reset all states
      setIsProcessingReset(false);
      setIsResetFlow(false);
      setResetToken(null);
      
      // Sign out and redirect to login
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Password validation effect
  useEffect(() => {
    setPasswordValidation({
      length: registerPassword.length >= 8,
      number: /\d/.test(registerPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(registerPassword)
    });
  }, [registerPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await signIn(loginEmail, loginPassword);
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast({
        title: "Invalid Password",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const metadata = {
        full_name: registerName,
        first_name: registerName.split(" ")[0],
        last_name: registerName.split(" ").slice(1).join(" "),
        company: registerCompany,
      };
      
      await signUp(registerEmail, registerPassword, metadata);
      
      // Clear registration form
      setRegisterName("");
      setRegisterEmail("");
      setRegisterPassword("");
      setRegisterCompany("");
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSocialLogin = async (provider: "google" | "github") => {
    try {
      await signInWithSocial(provider);
    } catch (error) {
      console.error(`${provider} login error:`, error);
    }
  };
  
  // If still loading authentication state, show loading indicator
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Show password reset form if in reset flow
  if (isResetFlow) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-accent">
              Market<span className="text-primary">Eye</span>
            </h1>
            <p className="text-muted-foreground mt-2">
              Reset your password
            </p>
          </div>

          <Card>
            <form onSubmit={handlePasswordReset}>
              <CardHeader>
                <CardTitle>Create New Password</CardTitle>
                <CardDescription>
                  Please enter and confirm your new password
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <TooltipProvider>
                    <Tooltip open={isPasswordFocused}>
                      <TooltipTrigger asChild>
                        <div className="relative">
                          <Input
                            id="new-password"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            onFocus={() => setIsPasswordFocused(true)}
                            onBlur={() => setIsPasswordFocused(false)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handlePasswordReset(e);
                              }
                            }}
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            tabIndex={-1}
                          >
                            {showNewPassword ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="p-0">
                        <div className="space-y-2 p-3">
                          <p className="font-semibold">Password must:</p>
                          <ul className="space-y-1">
                            <li className={`flex items-center gap-2 ${resetPasswordValidation.length ? 'text-green-500' : 'text-red-500'}`}>
                              {resetPasswordValidation.length ? <Check size={16} /> : <X size={16} />}
                              Be at least 8 characters long
                            </li>
                            <li className={`flex items-center gap-2 ${resetPasswordValidation.number ? 'text-green-500' : 'text-red-500'}`}>
                              {resetPasswordValidation.number ? <Check size={16} /> : <X size={16} />}
                              Include at least one number
                            </li>
                            <li className={`flex items-center gap-2 ${resetPasswordValidation.special ? 'text-green-500' : 'text-red-500'}`}>
                              {resetPasswordValidation.special ? <Check size={16} /> : <X size={16} />}
                              Include at least one special character
                            </li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handlePasswordReset(e);
                        }
                      }}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full">
                  Reset Password
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-accent">
            Market<span className="text-primary">Eye</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Competitive benchmarking dashboard for marketing teams
          </p>
        </div>
        
        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <Card>
              <form onSubmit={handleLogin}>
                <CardHeader>
                  <CardTitle>Login</CardTitle>
                  <CardDescription>
                    Enter your credentials to access your dashboard
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <Button
                        type="button"
                        variant="link"
                        className="px-0 text-sm text-accent hover:underline"
                        tabIndex={-1}
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!loginEmail) {
                            toast({
                              title: "Email Required",
                              description: "Please enter your email address first.",
                              variant: "destructive",
                            });
                            return;
                          }
                          try {
                            const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
                              redirectTo: `${window.location.origin}/auth`
                            });
                            if (error) throw error;
                            toast({
                              title: "Password Reset Email Sent",
                              description: "Please check your email for password reset instructions.",
                            });
                          } catch (error: any) {
                            toast({
                              title: "Error",
                              description: error.message || "Failed to send reset email. Please try again.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Forgot Password?
                      </Button>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleLogin(e);
                          }
                        }}
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        tabIndex={-1}
                      >
                        {showLoginPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        type="button"
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleSocialLogin("google")}
                      >
                        <svg viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        Google
                      </Button>
                      <Button 
                        type="button"
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleSocialLogin("github")}
                      >
                        <Github className="mr-2 h-4 w-4" />
                        GitHub
                      </Button>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
          
          <TabsContent value="register">
            <Card>
              <form onSubmit={handleRegister}>
                <CardHeader>
                  <CardTitle>Create an account</CardTitle>
                  <CardDescription>
                    Sign up for a free trial of MarketEye
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="register-name"
                      placeholder="John Smith"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-company">Company Name</Label>
                    <Input
                      id="register-company"
                      placeholder="Your Company (Optional)"
                      value={registerCompany}
                      onChange={(e) => setRegisterCompany(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <TooltipProvider>
                      <Tooltip open={isPasswordFocused}>
                        <TooltipTrigger asChild>
                          <div className="relative">
                            <Input
                              id="register-password"
                              type={showRegisterPassword ? "text" : "password"}
                              value={registerPassword}
                              onChange={(e) => setRegisterPassword(e.target.value)}
                              onFocus={() => setIsPasswordFocused(true)}
                              onBlur={() => setIsPasswordFocused(false)}
                              required
                              className="w-full"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            >
                              {showRegisterPassword ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="p-0">
                          <div className="space-y-2 p-3">
                            <p className="font-semibold">Password must:</p>
                            <ul className="space-y-1">
                              <li className={`flex items-center gap-2 ${passwordValidation.length ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.length ? <Check size={16} /> : <X size={16} />}
                                Be at least 8 characters long
                              </li>
                              <li className={`flex items-center gap-2 ${passwordValidation.number ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.number ? <Check size={16} /> : <X size={16} />}
                                Include at least one number
                              </li>
                              <li className={`flex items-center gap-2 ${passwordValidation.special ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.special ? <Check size={16} /> : <X size={16} />}
                                Include at least one special character
                              </li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        type="button"
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleSocialLogin("google")}
                      >
                        <svg viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        Google
                      </Button>
                      <Button 
                        type="button"
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleSocialLogin("github")}
                      >
                        <Github className="mr-2 h-4 w-4" />
                        GitHub
                      </Button>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="text-center text-sm text-muted-foreground">
          By continuing, you agree to MarketEye's{" "}
          <a href="/terms" className="text-primary hover:underline">Terms of Service</a>{" "}
          and{" "}
          <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
};

export default Auth;
